/** Pure view helpers for the B2G outreach waves and markets (campaign console, `waves` engine). */
import type { PillTone } from "../components/Pill";
import type { AutoReplyMark, Market, OutreachMessage, Wave, WaveStatus } from "../api/b2g";

export const WAVE_FILTERS: { value: string; label: string }[] = [
  { value: "open", label: "Needs you" },
  { value: "active", label: "Sending" },
  { value: "all", label: "All" },
];

export const OPEN_STATUSES: WaveStatus[] = ["pending_approval"];
/** A version waiting for the check or the owner. */
export const REVIEW_STATUSES: WaveStatus[] = ["checking", "pending_approval", "changes_requested"];

/** An update of a wave the owner approved before: it is stopped, never rejected. */
export function isRunningUpdate(w: Wave): boolean {
  return Boolean(w.approved_at) && REVIEW_STATUSES.includes(w.status);
}

/** How many unsent emails this version changed or added. */
export function revisionCounts(messages: OutreachMessage[]): { changed: number; added: number } {
  let changed = 0;
  let added = 0;
  for (const m of messages) {
    if (m.revision?.state === "changed") changed += 1;
    if (m.revision?.state === "new") added += 1;
  }
  return { changed, added };
}

/** One word for a recipient's row: "new" when every unsent email is new, "changed" when any differs. */
export function recipientRevision(steps: OutreachMessage[]): "new" | "changed" | null {
  const states = steps.map((s) => s.revision?.state).filter(Boolean);
  if (!states.length) return null;
  if (states.length === steps.length && states.every((s) => s === "new")) return "new";
  return states.some((s) => s === "new" || s === "changed") ? "changed" : null;
}
export const ACTIVE_STATUSES: WaveStatus[] = ["approved", "sending", "paused"];

export function waveStatusTone(status: WaveStatus): PillTone {
  if (status === "pending_approval") return "signal";
  if (status === "approved" || status === "sending" || status === "completed") return "ok";
  if (status === "rejected" || status === "stopped") return "alert";
  return "muted";
}

export function marketStatusTone(m: Market): PillTone {
  if (m.status === "excluded") return "alert";
  if (m.campaign_excluded) return "signal";
  if (m.status === "active") return "ok";
  return "muted";
}

export function travelTone(m: Market): PillTone {
  if (m.travel_verdict === "no_travel") return "alert";
  if (m.travel_verdict === "owner_ok") return "signal";
  if (m.travel_verdict === "ok") return "ok";
  return "muted";
}

export const TRAVEL_LABEL: Record<string, string> = {
  unknown: "not checked",
  no_travel: "no travel",
  owner_ok: "travel only with your OK",
  ok: "travel ok",
};

export function checkTone(status: OutreachMessage["check_status"]): PillTone {
  if (status === "ok" || status === "skipped") return "ok";
  if (status === "pending") return "muted";
  return "alert";
}

export function messageTone(m: OutreachMessage): PillTone {
  if (m.outcome === "replied") return "ok";
  if (m.status === "bounced" || m.outcome === "bounced" || m.status === "failed") return "alert";
  if (m.status === "sent") return "ok";
  if (m.status === "stopped") return "muted";
  return "signal";
}

export function messageLabel(m: OutreachMessage): string {
  if (m.outcome === "replied") return "replied";
  if (m.status === "bounced" || m.outcome === "bounced") return "bounced";
  if (m.status === "stopped") return `stopped · ${(m.stop_reason ?? "").replace(/_/g, " ")}`;
  if (m.status === "drafted") return "draft in Outlook";
  return m.status;
}

export interface RecipientGroup {
  contactId: string;
  first: OutreachMessage;
  steps: OutreachMessage[];
}

/** One row per recipient, steps in order — the wave detail's table. */
export function groupByRecipient(messages: OutreachMessage[]): RecipientGroup[] {
  const map = new Map<string, OutreachMessage[]>();
  for (const m of messages) {
    const list = map.get(m.contact_id) ?? [];
    list.push(m);
    map.set(m.contact_id, list);
  }
  return [...map.entries()].map(([contactId, steps]) => {
    const sorted = [...steps].sort((a, b) => a.step - b.step);
    return { contactId, first: sorted[0], steps: sorted };
  });
}

export const STEP_LABEL = ["First email", "Follow-up 1", "Follow-up 2"];

export const LANGUAGE_NAMES: Record<string, string> = {
  de: "German", en: "English", tr: "Turkish", az: "Azerbaijani", ru: "Russian", uz: "Uzbek",
  kk: "Kazakh", ky: "Kyrgyz", ar: "Arabic", fr: "French", ka: "Georgian", hy: "Armenian",
  ro: "Romanian", bg: "Bulgarian", sr: "Serbian", sq: "Albanian", mk: "Macedonian", el: "Greek",
  fa: "Persian", ur: "Urdu", id: "Indonesian", ms: "Malay", vi: "Vietnamese", th: "Thai",
};

export function languageName(code: string): string {
  return LANGUAGE_NAMES[code] ?? code.toUpperCase();
}

const REGION_NAMES = new Intl.DisplayNames(["en"], { type: "region" });

/** "KZ" → "Kazakhstan"; an unknown code comes back as it is. */
export function countryName(iso2: string): string {
  try {
    return REGION_NAMES.of(iso2.toUpperCase()) ?? iso2;
  } catch {
    return iso2;
  }
}

const WHOLE = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/** 285_721_236 → "286 M", 1_243_741 → "1.24 M", 1_410_000_000 → "1.41 bn" */
export function formatPopulation(n: number): string {
  if (n >= 999_500_000) return `${(n / 1e9).toPrecision(3)} bn`;
  if (n >= 1e6) return `${(n / 1e6).toPrecision(3)} M`;
  return WHOLE.format(n);
}

/** 13125 → "13,125 USD" */
export function formatUsd(n: number): string {
  return `${WHOLE.format(n)} USD`;
}

/** 5_050_886_599_504 → "5.05 tn USD", 472_209_123_750 → "472 bn USD", 812_400_000 → "812 M USD" */
export function formatUsdTotal(n: number): string {
  if (n >= 999_500_000_000) return `${(n / 1e12).toPrecision(3)} tn USD`;
  if (n >= 999_500_000) return `${(n / 1e9).toPrecision(3)} bn USD`;
  if (n >= 1e6) return `${(n / 1e6).toPrecision(3)} M USD`;
  return formatUsd(n);
}

/** A market's population and GDP per capita against Germany's, one line each – the hover text of a country label. */
export function economyLines(m: Market): string[] {
  const lines: string[] = [];
  if (m.population !== null) lines.push(`${formatPopulation(m.population)} inhabitants`);
  if (m.gdp_per_capita_usd !== null) {
    lines.push(`GDP per capita: ${formatUsd(m.gdp_per_capita_usd)} (${percent(m.gdp_per_capita_vs_germany)} of Germany)`);
  }
  if (lines.length && m.economy_year !== null) lines.push(`World Bank ${m.economy_year}`);
  return lines;
}

export type MarketSortKey = "score" | "population" | "gdp" | "gdp_per_capita";
export type SortDir = "asc" | "desc";

const SORT_VALUE: Record<MarketSortKey, (m: Market) => number | null> = {
  score: (m) => m.score,
  population: (m) => m.population,
  gdp: (m) => m.gdp_usd,
  gdp_per_capita: (m) => m.gdp_per_capita_usd,
};

/** The markets table's order: excluded markets last, then the column (a market without a value after the others), then the name. */
export function sortMarkets(markets: Market[], key: MarketSortKey, dir: SortDir): Market[] {
  const value = SORT_VALUE[key];
  const sign = dir === "desc" ? -1 : 1;
  return [...markets].sort((a, b) => {
    const excluded = Number(a.status === "excluded") - Number(b.status === "excluded");
    if (excluded) return excluded;
    const va = value(a);
    const vb = value(b);
    if (va !== vb) {
      if (va === null) return 1;
      if (vb === null) return -1;
      return sign * (va - vb);
    }
    return a.name.localeCompare(b.name);
  });
}

export interface FigureTotal {
  total: number;
  /** How many of `markets` carry the figure. */
  counted: number;
  markets: number;
}

/** A figure (population, GDP) summed over the markets that are not excluded. */
export function figureTotal(markets: Market[], figure: (m: Market) => number | null): FigureTotal {
  const open = markets.filter((m) => m.status !== "excluded");
  const known = open.map(figure).filter((v): v is number => v !== null);
  return {
    total: known.reduce((sum, v) => sum + v, 0),
    counted: known.length,
    markets: open.length,
  };
}

/** What a header's Σ sums – "the 44 markets in the list", or how many of them carry no figure. */
export function totalScope(t: FigureTotal): string {
  return t.counted === t.markets
    ? `the ${t.markets} markets in the list`
    : `${t.counted} of the ${t.markets} markets in the list (the rest have no figure)`;
}

const DAY_MONTH = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

/** "2026-10-30" → "30 Oct" */
export function formatDayMonth(date: string): string {
  return DAY_MONTH.format(new Date(`${date}T00:00:00Z`));
}

/** The latest automatic reply the seat marked on a recipient's first email. */
export function latestAutoReply(first: OutreachMessage): AutoReplyMark | null {
  const marks = first.auto_replies ?? [];
  return marks.length ? marks[marks.length - 1] : null;
}

/** 0.217 → "22 %"; below 1 % one decimal (0.0033 → "0.3 %"), so a small economy keeps its size. */
export function percent(rate: number | null): string {
  if (rate === null) return "–";
  if (rate > 0 && rate < 0.0095) return `${(rate * 100).toFixed(1)} %`;
  return `${Math.round(rate * 100)} %`;
}

export type LetterState = "none" | "needs_signature" | "signed" | "no_signature_needed";

export function letterState(m: OutreachMessage): LetterState {
  if (!m.letter) return "none";
  if (m.letter.signed) return "signed";
  return m.letter.signature_required ? "needs_signature" : "no_signature_needed";
}

/** The step's letter waits for the owner's signature – an unsigned letter only blocks while its email can still go out. */
export function letterAwaitsSignature(m: OutreachMessage): boolean {
  return letterState(m) === "needs_signature" && (m.status === "pending" || m.status === "drafted");
}

/** Letters, signed ones, and the unsent ones still waiting for the owner's signature. */
export function letterCounts(messages: OutreachMessage[]): { letters: number; signed: number; toSign: number } {
  let letters = 0;
  let signed = 0;
  let toSign = 0;
  for (const m of messages) {
    if (!m.letter) continue;
    letters += 1;
    if (m.letter.signed) signed += 1;
    if (letterAwaitsSignature(m)) toSign += 1;
  }
  return { letters, signed, toSign };
}

/** Why the wave cannot be approved yet, or null – the server's 409 text, which refuses the approval anyway. */
export function letterBlock(messages: OutreachMessage[]): string | null {
  const { toSign } = letterCounts(messages);
  return toSign ? `${toSign} letter(s) still need your signature` : null;
}

/** "1 letter needs your signature" / "3 letters need your signature" */
export function lettersToSignText(n: number): string {
  return n === 1 ? "1 letter needs your signature" : `${n} letters need your signature`;
}

/** The status pill of a step's letter. An unsigned letter whose email no longer goes out reads "not signed". */
export function letterPill(m: OutreachMessage): { label: string; tone: PillTone } | null {
  const state = letterState(m);
  if (state === "none") return null;
  if (state === "signed") return { label: "signed", tone: "ok" };
  if (state === "no_signature_needed") return { label: "no signature needed", tone: "muted" };
  return letterAwaitsSignature(m) ? { label: "needs your signature", tone: "signal" } : { label: "not signed", tone: "muted" };
}

/** A recipient's CC addresses across its steps, and the CC before this version when it changed them. */
export function recipientCc(steps: OutreachMessage[]): { cc: string[]; before: string[] | null } {
  const cc = [...new Set(steps.flatMap((s) => s.cc ?? []))];
  const changed = steps.find((s) => s.revision?.cc_before);
  return { cc, before: changed?.revision?.cc_before ?? null };
}

/** "820 B", "312 KB", "1.4 MB" */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}
